import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { SheetKicker } from '@/components/ui/sheet'
import { RangeWordsContext, type RangeWords } from '@/components/property/calendar/range-words'
import { hasUnsettledBlock, usePropertyBlocks } from '@/hooks/use-property-blocks'
import { CalendarSheets } from '@/components/property/calendar/calendar-sheets'
import { parseDayOpen } from '@/components/property/calendar/day-list'
import type { CalendarLayerId } from '@/components/property/calendar/layers'
import { deriveReadiness } from '@/components/property/calendar/readiness'
import {
  parseCleaningOpen,
  parseStayOpen,
  type CalendarSheetNav,
} from '@/components/property/calendar/sheet-open'
import { useCalendarData } from '@/components/property/calendar/use-calendar-data'
import type { PortfolioProperty } from './merge'
import {
  dayCoverage,
  nextDayIso,
  propertyCalendarOf,
  sheetReadsBlocks,
  type SheetWindow,
} from './sheet-calendar'

interface PortfolioSheetsProps {
  propertyId: string
  /** Every property as merged across the windows read. */
  properties: readonly PortfolioProperty[]
  /** The windows the grid spans, read or not. */
  windows: readonly SheetWindow[]
  isVisible: (id: CalendarLayerId) => boolean
  nav: CalendarSheetNav
}

/** "Not read" for the day list, in the portfolio's words: there is no month here to go to. */
const OUTSIDE_MESSAGE = 'This day is not loaded on the calendar. Scroll to it to see what is on it.'

/** The portfolio reads windows of days as it scrolls: the sheets name those, not a month. */
const LOADED_DAYS_WORDS: RangeWords = {
  shown: 'the dates loaded on the calendar',
  deliveriesHeading: 'Deliveries',
  gapContinues: (before, after) =>
    before && after
      ? 'This gap also runs past both ends of the dates loaded on the calendar.'
      : before
        ? 'This gap also starts before the dates loaded on the calendar.'
        : 'This gap also runs past the dates loaded on the calendar.',
}

/**
 * The sheet open on the portfolio calendar (CAP-1418): the property Calendar
 * tab's own sheets (`CalendarSheets`), over the grid, for the property named
 * in the URL, each headed with that property's name.
 *
 * Everything a sheet shows of the calendar comes from what the grid has
 * already read for that property (`propertyCalendarOf`): no calendar read of
 * its own. What the grid does not read is asked for here, for this property
 * alone and only for the sheets that show it: the crew list (a cleaning's crew
 * name) and the property's blocks in full (a block's panel, a day's blocks,
 * the overlap note). Mounted only while a sheet is open.
 *
 * After an action in a sheet (a cleaning changed, a block made or released)
 * the sheet's own mutation invalidates `['portfolio-calendar']` and the
 * property's calendar: the windows the grid spans are read again (others are
 * marked stale) and the grid redraws from the answer, never from a guess.
 */
export function PortfolioSheets({
  propertyId,
  properties,
  windows,
  isVisible,
  nav,
}: PortfolioSheetsProps) {
  const property = properties.find(candidate => candidate.property_id === propertyId)
  // A new read of the property only when a window's answer changes: the
  // windows array itself is new every render.
  const readsKey = windows.map(window => `${window.from}:${window.updatedAt}`).join('|')
  const calendar = useMemo(
    () => (property ? propertyCalendarOf(property, windows) : undefined),
    // `readsKey` is the windows' data version.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [property, readsKey]
  )

  const { open } = nav
  // Asked for only by the sheets that show them.
  const needsCrew = !!(parseStayOpen(open) || parseCleaningOpen(open) || parseDayOpen(open))
  const needsBlocks = sheetReadsBlocks(open)
  const teamsQuery = useQuery({
    queryKey: ['cleaning', 'teams'],
    queryFn: () => api.getCleaningTeams(),
    enabled: needsCrew,
  })
  const teams = useMemo(() => teamsQuery.data?.teams ?? [], [teamsQuery.data])
  const crewLoading = needsCrew && teamsQuery.isPending
  const blocksQuery = usePropertyBlocks(propertyId, { enabled: needsBlocks })

  const range = useMemo(
    () =>
      calendar ? { start: calendar.from, end: nextDayIso(calendar.to) } : { start: '', end: '' },
    [calendar]
  )
  const data = useCalendarData({ calendar, range, blocksData: blocksQuery.data, isVisible })
  const readiness = deriveReadiness({
    month: (calendar?.from ?? '1970-01-01').slice(0, 7),
    calendar,
    isPlaceholderData: false,
    monthQuery: { isPending: !calendar, isError: false },
    teamsQuery: { isPending: crewLoading, isError: teamsQuery.isError },
    isVisible,
  })

  // Nothing to say until the property's row has been read; a property no
  // window lists (no longer active) has no sheet here.
  if (!property) return null

  return (
    <SheetKicker value={property.name}>
      <RangeWordsContext.Provider value={LOADED_DAYS_WORDS}>
        <CalendarSheets
          propertyId={propertyId}
          propertyName={property.name}
          calendar={calendar}
          isPlaceholderData={false}
          readiness={readiness}
          data={data}
          range={range}
          isVisible={isVisible}
          teams={teams}
          crewLoading={crewLoading}
          blocksQuery={blocksQuery}
          nav={nav}
          dayListState={openDay => (calendar ? dayCoverage(windows, openDay) : 'pending')}
          outsideMessage={OUTSIDE_MESSAGE}
        />
      </RangeWordsContext.Provider>
    </SheetKicker>
  )
}

interface BlockSettleWatchProps {
  propertyId: string
  /** Whether a sheet reading this property's blocks is open now: it is never let go then. */
  sheetOpen: boolean
  onSettled: (propertyId: string) => void
}

/**
 * Keeps a property's blocks read alive after its block sheet closes, until no
 * block is syncing. A block made or removed from a sheet is "pending" or
 * "deleting" until Uplisting confirms it; `usePropertyBlocks` re-reads the
 * blocks meanwhile and, once they settle, reads the calendar windows again.
 * The sheets close as soon as the write lands, so without this the grid would
 * keep the block as it was until something else read it again. Renders
 * nothing.
 */
export function BlockSettleWatch({ propertyId, sheetOpen, onSettled }: BlockSettleWatchProps) {
  const query = usePropertyBlocks(propertyId)
  const settled = query.data !== undefined && !hasUnsettledBlock(query.data)
  const failed = query.data === undefined && query.isError
  useEffect(() => {
    if (!sheetOpen && (settled || failed)) onSettled(propertyId)
  }, [sheetOpen, settled, failed, propertyId, onSettled])
  return null
}
